/**
 * Movement keys by `KeyboardEvent.code`, laid out like the Touch controllers: the left hand's WASD is the left stick
 * (W/S rise/sink, A/D turn), the right hand's arrows are the right stick (Up/Down forward/back, Left/Right sideways),
 * Shift is the left grip's boost and Space stops.
 */
export const XR_KEYBOARD_MOVEMENT = {
    KeyW: 'rise', KeyS: 'sink', KeyA: 'turn left', KeyD: 'turn right',
    ArrowUp: 'forward', ArrowDown: 'back', ArrowLeft: 'left', ArrowRight: 'right',
    ShiftLeft: 'faster', ShiftRight: 'faster', Space: 'stop',
};
export const XR_KEYBOARD_MOVEMENT_LEGEND = [
    'Tap to cruise, tap again to stop | Space: stop all',
    'W/S: rise/sink | A/D: turn (left stick)',
    'Up/Down: forward/back | Left/Right: sideways (right stick)',
    'Shift: faster on/off',
];
// Opposite keys share an axis; keys sharing a stick lock like the left stick, which drives one axis at a time.
const CRUISE = {
    KeyW: { axis: 'vertical', stick: 'left' }, KeyS: { axis: 'vertical', stick: 'left' },
    KeyA: { axis: 'turn', stick: 'left' }, KeyD: { axis: 'turn', stick: 'left' },
    ArrowUp: { axis: 'forward', stick: 'forward' }, ArrowDown: { axis: 'forward', stick: 'forward' },
    ArrowLeft: { axis: 'sideways', stick: 'sideways' }, ArrowRight: { axis: 'sideways', stick: 'sideways' },
    ShiftLeft: { axis: 'boost', stick: 'boost' }, ShiftRight: { axis: 'boost', stick: 'boost' },
};
/**
 * Quest Browser stutters for as long as a key is held in WebXR, so movement keys latch on tap instead of acting while
 * held: a tap starts cruising that way, tapping it again or its opposite stops that axis, and Space stops everything.
 */
export function toggleXrKeyboardCruise(active, code) {
    const target = CRUISE[code];
    if (!target)
        return new Set();
    const next = new Set(active);
    if (next.has(code)) {
        next.delete(code);
        return next;
    }
    const opposite = [...next].filter(other => CRUISE[other]?.axis === target.axis);
    if (opposite.length) {
        opposite.forEach(other => next.delete(other));
        return next;
    }
    [...next].filter(other => CRUISE[other]?.stick === target.stick).forEach(other => next.delete(other));
    next.add(code);
    return next;
}
const axis = (active, negative, positive) => (active.has(positive) ? 1 : 0) - (active.has(negative) ? 1 : 0);
export function resolveXrKeyboardSticks(active) {
    return {
        leftX: axis(active, 'KeyA', 'KeyD'),
        leftY: axis(active, 'KeyW', 'KeyS'),
        rightX: axis(active, 'ArrowLeft', 'ArrowRight'),
        rightY: axis(active, 'ArrowUp', 'ArrowDown'),
        boost: active.has('ShiftLeft') || active.has('ShiftRight') ? 1 : 0,
    };
}
/**
 * Keys are binary, so cruising ramps like a gently pushed stick: deflection rises over `riseSeconds`, falls over
 * `fallSeconds`, turning tops out at `turnScale` and Shift's boost builds over `boostSeconds`.
 */
export const XR_KEYBOARD_EASE = { riseSeconds: 0.4, fallSeconds: 0.4, turnScale: 0.55, boostSeconds: 0.9, maxDelta: 0.1 };
export const XR_KEYBOARD_IDLE = { leftX: 0, leftY: 0, rightX: 0, rightY: 0, boost: 0 };
const slew = (current, target, rise, fall, dt) => {
    const rising = target !== 0 && (current === 0 || Math.sign(target) === Math.sign(current)) && Math.abs(target) > Math.abs(current);
    const step = dt / (rising ? rise : fall);
    return current < target ? Math.min(target, current + step) : Math.max(target, current - step);
};
/** Advances linear key ramps toward the held keys; feed the result to `easeXrKeyboardSticks`. */
export function stepXrKeyboardSticks(previous, held, delta) {
    const dt = Math.min(Math.max(0, delta), XR_KEYBOARD_EASE.maxDelta);
    const { riseSeconds: rise, fallSeconds: fall } = XR_KEYBOARD_EASE;
    return {
        leftX: slew(previous.leftX, held.leftX, rise, fall, dt),
        leftY: slew(previous.leftY, held.leftY, rise, fall, dt),
        rightX: slew(previous.rightX, held.rightX, rise, fall, dt),
        rightY: slew(previous.rightY, held.rightY, rise, fall, dt),
        boost: slew(previous.boost, held.boost, XR_KEYBOARD_EASE.boostSeconds, fall, dt),
    };
}
const ease = (value) => {
    const x = Math.min(1, Math.abs(value));
    return Math.sign(value) * x * x * (3 - 2 * x);
};
/** Smoothstep-shaped stick deflection from linear key ramps, with the turn rate scaled down. */
export function easeXrKeyboardSticks(ramp) {
    return {
        leftX: ease(ramp.leftX) * XR_KEYBOARD_EASE.turnScale,
        leftY: ease(ramp.leftY),
        rightX: ease(ramp.rightX),
        rightY: ease(ramp.rightY),
        boost: ease(ramp.boost),
    };
}
/** Adds keyboard deflection to a controller stick without exceeding full deflection. */
export const combineXrStick = (stick, keys) => Math.max(-1, Math.min(1, stick + keys));
